import {
  CORE_HP_DEFAULT,
  MAX_DROPS,
  R_PLANET,
  MAX_EFFECTS,
  MAX_ENEMIES,
  MAX_PROJECTILES,
  R_ORBIT,
  R_STATION,
  TAU,
} from './constants';
import { makeRng, rand } from './rng';
import { queueWave } from './systems/spawning';
import type {
  Content,
  Drone,
  Drop,
  Effect,
  EffectKind,
  Enemy,
  EnemyDef,
  EvolutionChoice,
  LevelDef,
  PassiveDef,
  PlanetDef,
  Projectile,
  Slot,
  StructureDef,
  UltimateDef,
  World,
} from './types';

export interface WorldOptions {
  content: Content;
  planetId: string;
  levelId: string;
  seed: number;
  /** Evolution branch ids the player has chosen, e.g. ['pyre', 'twin-vent']. */
  evolutions?: readonly string[];
  /**
   * Structures bought with jewels. Starters (jewelCost 0) are always available
   * and don't need listing.
   */
  unlockedStructures?: readonly string[];
}

/**
 * Evolution branches are shallow patches over the base planet. Applying them
 * here means the sim only ever sees one flattened definition — no branch
 * checks scattered through the systems.
 */
export function resolveEvolutions(
  planet: PlanetDef,
  chosen: readonly string[],
): { passive: PassiveDef; ultimate: UltimateDef; choices: EvolutionChoice[] } {
  const all = [...planet.evolutions.l4, ...planet.evolutions.l8];
  const choices = all.filter((c) => chosen.includes(c.id));
  let passive: PassiveDef = { ...planet.passive };
  let ultimate: UltimateDef = { ...planet.ultimate };
  for (const c of choices) {
    if (c.passive) passive = { ...passive, ...c.passive };
    if (c.ultimate) ultimate = { ...ultimate, ...c.ultimate };
  }
  return { passive, ultimate, choices };
}

/** Merged body/palette for the renderer, after evolution art patches (§5.1). */
export function resolveAppearance(
  planet: PlanetDef,
  chosen: readonly string[],
): { body: PlanetDef['body']; palette: PlanetDef['palette'] } {
  const all = [...planet.evolutions.l4, ...planet.evolutions.l8];
  let body = { ...planet.body };
  let palette = { ...planet.palette };
  for (const c of all) {
    if (!chosen.includes(c.id)) continue;
    if (c.body) body = { ...body, ...c.body };
    if (c.palette) palette = { ...palette, ...c.palette };
  }
  return { body, palette };
}

/** Mount counts, after the planet's passive and any cap this level imposes. */
function surfaceCount(planet: PlanetDef, passive: PassiveDef, level: LevelDef): number {
  if (passive.noSurfaceSlots) return 0;
  return Math.min(planet.slots.surface, level.mounts?.surface ?? Infinity);
}

function ringCount(planet: PlanetDef, passive: PassiveDef, level: LevelDef): number {
  const base = planet.slots.ring + (passive.extraRingSlots ?? 0);
  return Math.min(base, level.mounts?.ring ?? Infinity);
}

function makeSlots(planet: PlanetDef, passive: PassiveDef, level: LevelDef): Slot[] {
  const slots: Slot[] = [];
  let id = 0;

  const add = (layer: Slot['layer'], count: number, offset: number): void => {
    for (let i = 0; i < count; i++) {
      slots.push({
        id: id++,
        layer,
        baseTheta: (i / count) * TAU + offset / Math.max(count, 1),
        structure: null,
        locked: false,
        lockTheta: 0,
      });
    }
  };

  // Surface slots offset by half a step so none starts dead north, under the
  // HUD placard.
  add('surface', surfaceCount(planet, passive, level), TAU / 2);
  add('ring', ringCount(planet, passive, level), 0);

  // Stations are placed, not spaced: the level names each bearing, because a
  // station is an answer to that level's attack pattern and evenly spacing
  // them would make it a generic one.
  for (const spec of level.stations ?? []) {
    slots.push({
      id: id++,
      layer: 'station',
      baseTheta: spec.bearing,
      stationR: spec.r ?? R_STATION,
      structure: null,
      locked: false,
      lockTheta: 0,
    });
  }

  return slots;
}

function blankEnemy(id: number, def: EnemyDef): Enemy {
  return {
    id,
    alive: false,
    def,
    theta: 0,
    r: 0,
    bearing: 0,
    entryTheta: 0,
    phase: 'dead',
    phaseT: 0,
    stageDur: 4,
    phaseR: 0,
    phaseTheta: 0,
    hp: 1,
    maxHp: 1,
    speed: 0,
    stunT: 0,
    freezeT: 0,
    slowT: 0,
    slowMult: 1,
    burnT: 0,
    burnDps: 0,
    timer: 0,
    veered: false,
    underRing: false,
    cloaked: false,
    revealed: false,
    phaseIndex: 0,
    wobble: 0,
  };
}

/** Everything the player can build: starters plus whatever jewels have bought. */
function resolveUnlocked(opts: WorldOptions): ReadonlySet<string> {
  const set = new Set(opts.unlockedStructures ?? []);
  for (const def of opts.content.structures.values()) {
    if (def.jewelCost === 0) set.add(def.id);
  }
  return set;
}

export function createWorld(opts: WorldOptions): World {
  const planet = opts.content.planets.get(opts.planetId);
  if (!planet) throw new Error(`unknown planet: ${opts.planetId}`);
  const level = opts.content.levels.get(opts.levelId);
  if (!level) throw new Error(`unknown level: ${opts.levelId}`);

  const { passive, ultimate } = resolveEvolutions(planet, opts.evolutions ?? []);
  const rng = makeRng(opts.seed);

  const world: World = {
    tick: 0,
    time: 0,
    rng,
    seed: opts.seed,

    planet,
    passive,
    ultimate,
    level,
    modifiers: new Set(level.modifiers),

    planetRot: 0,
    ringRot: 0,
    ringVel: 0,
    ringR: R_ORBIT + (passive.ringRadiusBonus ?? 0),

    slots: makeSlots(planet, passive, level),
    enemies: [],
    projectiles: [],
    effects: [],
    drones: [],
    drops: [],

    juice: level.startingJuice,
    coreHp: planet.coreHp || CORE_HP_DEFAULT,
    coreMax: planet.coreHp || CORE_HP_DEFAULT,
    coreDamageTaken: 0,
    upgrades: { ...(level.startingUpgrades ?? {}) },
    unlocked: resolveUnlocked(opts),

    waveIndex: 0,
    waveState: 'prep',
    waveTimer: level.waves[0]?.prep ?? 0,
    pending: [],

    ultCd: ultimate.cooldown,
    ultAim: null,

    stats: {
      kills: 0,
      juiceEarned: 0,
      juiceSpent: 0,
      juiceLost: 0,
      ultsFired: 0,
      ringDistance: 0,
    },

    events: [],
    nextId: 1,
  };

  // A level that grants upgrades up front needs its derived stats to match.
  world.coreMax = coreMaxFor(world);
  world.coreHp = world.coreMax;

  // Pre-allocate the pools. No allocation in the hot loop (§12.3).
  const firstEnemy = level.waves[0]?.groups[0]?.enemy;
  const placeholder =
    (firstEnemy ? opts.content.enemies.get(firstEnemy) : undefined) ??
    [...opts.content.enemies.values()][0];
  if (!placeholder) throw new Error('content has no enemy definitions');

  for (let i = 0; i < MAX_ENEMIES; i++) world.enemies.push(blankEnemy(i, placeholder));
  for (let i = 0; i < MAX_PROJECTILES; i++) {
    world.projectiles.push({
      id: i,
      alive: false,
      theta: 0,
      r: 0,
      vTheta: 0,
      vR: 0,
      damage: 0,
      splash: 0,
      life: 0,
      target: -1,
      kind: 'bolt',
    });
  }
  for (let i = 0; i < MAX_EFFECTS; i++) {
    world.effects.push({
      id: i,
      alive: false,
      kind: 'pulse',
      theta: 0,
      r: 0,
      front: 0,
      spread: 0,
      t: 0,
      duration: 0,
      damage: 0,
      dps: 0,
      hit: new Set<number>(),
    });
  }
  for (let i = 0; i < MAX_DROPS; i++) {
    world.drops.push({
      id: i,
      alive: false,
      theta: 0,
      r: 0,
      value: 0,
      life: 0,
      maxLife: 1,
      vR: 0,
      collected: false,
      collectT: 0,
    });
  }

  // Queue wave one now that the pools exist: the opening build phase shows
  // where it's coming from, and that has to be the bearings the wave will
  // actually use — random groups included.
  queueWave(world, opts.content);

  return world;
}

/* --- Planet upgrades ------------------------------------------------ *
 *
 * Bought with juice during a build phase and reset every run, so the opening
 * decision — reinforce the hull, or put it all into guns? — is live on every
 * level rather than being settled once in a meta screen.
 * ------------------------------------------------------------------- */

export function upgradeLevel(world: World, id: string): number {
  return world.upgrades[id] ?? 0;
}

/** Core Integrity ceiling, after Reinforced Hull. */
export function coreMaxFor(world: World): number {
  const base = world.planet.coreHp || CORE_HP_DEFAULT;
  return base + upgradeLevel(world, 'hull') * 30;
}

/**
 * Flat juice added to every pickup, from the planet's passive and the Mint
 * upgrade. Both are whole numbers so the player can do the arithmetic at a
 * glance — the point of the small-number economy.
 */
export function juiceBonusFor(world: World): number {
  return (world.passive.juiceBonus ?? 0) + upgradeLevel(world, 'mint');
}

/**
 * Auto-collect radius measured outward from the crust. 0 = no magnet.
 *
 * Level 3 reaches most of the way to the orbit ring, which is what the opening
 * level grants — between the magnet and the ring's own sweep, a new player
 * collects everything without being told to, and learns tapping later when the
 * radius shrinks and drops start timing out.
 */
export function magnetRadius(world: World): number {
  const level = upgradeLevel(world, 'magnet');
  return level === 0 ? 0 : 24 + level * 24;
}

/** Ring responsiveness, from the planet's passive and the Servos upgrade. */
export function ringSpeedFor(world: World): number {
  return (world.passive.ringSpeedMult ?? 1) * (1 + upgradeLevel(world, 'servos') * 0.15);
}

/* --- Pool acquisition ---------------------------------------------- */

export function acquireEnemy(world: World): Enemy | null {
  for (const e of world.enemies) if (!e.alive) return e;
  return null; // hard cap reached — the wave just gets shorter, never slower
}

export function acquireProjectile(world: World): Projectile | null {
  for (const p of world.projectiles) if (!p.alive) return p;
  return null;
}

export function acquireEffect(world: World, kind: EffectKind): Effect | null {
  for (const fx of world.effects) {
    if (!fx.alive) {
      fx.alive = true;
      fx.kind = kind;
      fx.t = 0;
      fx.front = 0;
      fx.hit.clear();
      return fx;
    }
  }
  return null;
}

export function acquireDrop(world: World): Drop | null {
  for (const d of world.drops) if (!d.alive) return d;
  return null;
}

export function acquireDrone(world: World): Drone {
  for (const d of world.drones) if (!d.alive) return d;
  const d: Drone = {
    id: world.drones.length,
    alive: false,
    theta: 0,
    r: 0,
    vTheta: 0,
    life: 0,
    cooldown: 0,
    damage: 0,
  };
  world.drones.push(d);
  return d;
}

/* --- Lookups ------------------------------------------------------- */

export function slotTheta(world: World, slot: Slot): number {
  // A station is bolted to the sky. Its bearing is absolute — that's the whole
  // point of it, and it's why every read of a slot's angle goes through here.
  if (slot.layer === 'station') return slot.baseTheta;
  if (slot.layer === 'ring') return slot.baseTheta + world.ringRot;
  if (slot.locked) return slot.lockTheta;
  return slot.baseTheta + world.planetRot;
}

/** The radius a slot's hardware sits at. */
export function slotRadius(world: World, slot: Slot): number {
  if (slot.layer === 'station') return slot.stationR ?? R_STATION;
  return slot.layer === 'ring' ? world.ringR : R_PLANET;
}

/**
 * Which catalogue a mount draws from. Stations are orbital hardware on a
 * gantry, so they take the ring's list — inventing a third catalogue would
 * mean three of everything for a difference the player can't see.
 */
export function slotBuildLayer(slot: Slot): 'surface' | 'ring' {
  return slot.layer === 'surface' ? 'surface' : 'ring';
}

export function structureDef(
  content: Content,
  state: { defId: string } | null,
): StructureDef | null {
  if (!state) return null;
  return content.structures.get(state.defId) ?? null;
}

/** Total mass mounted on the ring — drives inertia (§4.3). */
export function ringMass(world: World, content: Content): number {
  let mass = 1;
  for (const slot of world.slots) {
    if (slot.layer !== 'ring' || !slot.structure) continue;
    const def = content.structures.get(slot.structure.defId);
    const tier = def?.tiers[slot.structure.tier];
    mass += tier?.mass ?? 0.4;
  }
  return mass * world.planet.ringInertia;
}

export function jitterAngle(world: World, spread: number): number {
  return (rand(world.rng) - 0.5) * spread;
}

export function pushEvent(world: World, ev: World['events'][number]): void {
  // Cap the queue so a stalled consumer can't grow it without bound.
  if (world.events.length < 256) world.events.push(ev);
}

export type { LevelDef, PlanetDef };
